// THE NO-DOUBLE-COUNT RULE between Tech Daily Pay and Employee Pay.
//
// Tech Daily Pay (dailyLabor, read through utils/techDailyPay.js) IS the
// payroll for the techs. An employeePay entry is a record of a paycheck or
// other pay. When both describe the same days, only Tech Daily Pay counts:
//
//   An employeePay entry is NOT added to expense totals ("covered") when
//     - dailyPayCoverage === "covered"                      (set on Expenses)
//     - or it was created from Tech Daily Pay (createdFrom/source mentions
//       DailyLabor / Tech Daily Pay)
//     - or dailyPayCoverage is blank/"auto", its Pay Type is Regular or
//       Overtime (or blank), and the same person has Tech Daily Pay recorded
//       in the same calendar month as the entry's date.
//   It IS added when dailyPayCoverage === "separate", or it is a Bonus /
//   Other payment, or the person has no Tech Daily Pay that month.
//
// Any page that totals labor (Expenses, Cash Plan, Profit, Exports) can import
// these helpers so they all agree.

import { staffName } from "./techDailyPay";

export const COVERAGE_OPTIONS = [
  ["auto", "Automatic (Regular/Overtime pay is covered when the person has Tech Daily Pay that month)"],
  ["covered", "This pays days already on Tech Daily Pay — don't add it again"],
  ["separate", "Separate pay — add it on top of Tech Daily Pay"],
];

export const normPerson = (value) =>
  String(value || "")
    .toLowerCase()
    .replace(/[^a-z0-9]/g, "");

const monthOf = (date) => String(date || "").slice(0, 7);

/**
 * Index of who had Tech Daily Pay in which month.
 * `rows` = summarizeDailyLabor(...).rows (one row per person per day).
 */
export const buildDailyPayIndex = (rows = [], usersById = {}) => {
  const byUserMonth = new Map();
  const byNameMonth = new Map();
  rows.forEach((r) => {
    const month = monthOf(r.date);
    if (!month || !(Number(r.dailyPay) > 0)) return;
    if (r.userId) {
      const k = `${r.userId}|${month}`;
      byUserMonth.set(k, (byUserMonth.get(k) || 0) + Number(r.dailyPay));
    }
    const names = new Set([normPerson(r.name), normPerson(staffName(usersById[r.userId] || {}))].filter(Boolean));
    names.forEach((n) => {
      const k = `${n}|${month}`;
      byNameMonth.set(k, (byNameMonth.get(k) || 0) + Number(r.dailyPay));
    });
  });
  return { byUserMonth, byNameMonth };
};

const fromDailyPay = (row = {}) =>
  /daily\s*labor|tech\s*daily\s*pay|dailylabor/i.test(`${row.createdFrom || ""} ${row.source || ""}`);

/** { counted: boolean, reason: string } for one employeePay entry. */
export const employeePayCoverage = (row = {}, index = { byUserMonth: new Map(), byNameMonth: new Map() }) => {
  const choice = String(row.dailyPayCoverage || "auto");
  if (choice === "separate") return { counted: true, reason: "Separate pay (added)" };
  if (choice === "covered") return { counted: false, reason: "Marked as paying Tech Daily Pay days" };
  if (fromDailyPay(row)) return { counted: false, reason: "Created from Tech Daily Pay" };

  const payType = String(row.payType || "Regular").toLowerCase();
  if (payType !== "regular" && payType !== "overtime") return { counted: true, reason: "" };

  const month = monthOf(row.date);
  if (!month) return { counted: true, reason: "" };
  const hasDaily =
    (row.userId && index.byUserMonth.has(`${row.userId}|${month}`)) ||
    (normPerson(row.employeeName) && index.byNameMonth.has(`${normPerson(row.employeeName)}|${month}`));
  if (hasDaily) return { counted: false, reason: "Covered by Tech Daily Pay this month" };
  return { counted: true, reason: "" };
};
